import { Button } from '@/components/ui/button'
import { useAuthPageContext } from '@/features/auth/auth-context'
import { AUTH_STEP_QUERY_PARAMS, AUTH_STEPS, IAuthFormData } from '@/features/auth/helpers/auth-constants'
import { InputGroup } from '@/components/form/input-group'
import { useFormContext } from 'react-hook-form'
import { UrlConfig } from '@/setup/routing/routing'
import { datadogLogs } from '@datadog/browser-logs'
import { AuthInlineNavigation } from '@/features/auth/auth-inline-navigation'
import { useAuth } from '@clerk/nextjs'
import { useSignUp } from '@clerk/nextjs/legacy'
import { StepContainer } from '@/components/steps/step-container'
import { FixedBottomCtaBar } from '@/components/steps/fixed-bottom-cta-bar'
import { useWithLoading } from '@/utils/hooks/use-with-loading'
import Image from 'next/image'
import { useAttributionParams } from '@/features/attribution/utils/attribution-capture'
import { makeHandleClerkError } from '@/utils/clerk/handle-clerk-error'
import { ClerkAPIError } from '@clerk/nextjs/types'
import { useCallback, useEffect, useState } from 'react'
import { pushGTMEvent } from '@/features/third-party/google-tag-manager/google-tag-manager'
import { toast } from '@/features/toast/toast'
import { trackEvent, TrackEventName } from '@/utils/tracking/track'
import { useRouter, useSearchParams } from 'next/navigation'
import { getSafeRedirectUrl } from '@/utils/url/get-safe-redirect-url'
import { Separator } from '@/components/ui/separator'
import { interceptInAppBrowser, isInAppBrowser } from '@/utils/browser/is-in-app-browser'
import { GoogleOauthBrowserCallout } from '@/features/auth/components/google-oauth-browser-callout'
import { getLocalStorageItem, LOCAL_STORAGE_KEYS, setLocalStorageItem } from '@/setup/local-storage'
import { ATTRIBUTION_CONSTS, SIGN_UP_CONSTS } from 'shared'
import { clearBoardReferralCode, peekBoardReferralCode } from '@/features/auth/helpers/board-signup-referral'
import { splitFullName } from '@/utils/split-full-name'
import { AuthButtonWithLastUsedBadge } from '@/features/auth/components/auth-button-with-last-used-badge'
import { ATTRIBUTION_QUERY_PARAMS } from '@/features/attribution/utils/constants'

export const AuthSignupStep = () => {
    const router = useRouter()
    const { isLoaded: isAuthLoaded, isSignedIn } = useAuth()
    const { setCurrentStep, boardReferralCode } = useAuthPageContext()
    const { register, getValues } = useFormContext<IAuthFormData>()
    const { signUp } = useSignUp()
    const { trigger, setError } = useFormContext<IAuthFormData>()
    const searchParams = useSearchParams()

    // Check for OAuth redirect_url parameter (used by external OAuth clients like ChatGPT)
    // Validate the URL to prevent open redirect attacks
    const oauthRedirectUrl = getSafeRedirectUrl(searchParams.get('redirect_url'))

    const { clickId } = useAttributionParams()

    const routingUrl = UrlConfig['/routing'].getUrl({
        dub_id: clickId ?? undefined,
    })

    const [isWebView, setIsWebView] = useState(false)
    useEffect(() => {
        setIsWebView(isInAppBrowser())
    }, [])
    const lastUsedMethod = getLocalStorageItem<'google' | 'email'>(LOCAL_STORAGE_KEYS.Auth.LastUsedMethod)

    const { withLoading: withGoogleSignUpLoading, isLoading: isGoogleSignUpLoading } = useWithLoading()
    const { withLoading: withSignUpLoading, isLoading: isSignUpLoading } = useWithLoading()

    const makeHandleError = useCallback(
        (mode: 'toast' | 'inline-error') => (errors: ClerkAPIError[]) => {
            const firstError = errors[0]

            if (!firstError) return

            const message = firstError.message

            if (mode === 'toast') {
                toast.error(message)
            } else {
                setError('email', { message: message })
                datadogLogs.logger.error(message)
            }
        },
        [setError]
    )

    const handleSignup = withSignUpLoading(async () => {
        if (!signUp) return
        if (!isAuthLoaded) return
        if (isSignedIn) {
            if (oauthRedirectUrl) {
                window.location.assign(oauthRedirectUrl)
                return
            }
            router.replace(routingUrl)
            return
        }

        const isValid = await trigger(['email', 'name'])

        if (!isValid) return

        const { email, name } = getValues()
        const { getMetadata } = getMetadataMethods(boardReferralCode)

        setLocalStorageItem(LOCAL_STORAGE_KEYS.Auth.LastUsedMethod, 'email')

        // Track signup method selection in PostHog
        trackEvent(TrackEventName.SignupMethodSelected, {
            method: 'email',
        })

        pushGTMEvent({
            event: 'signed_up',
            user_email: email,
        })

        const { firstName, lastName } = splitFullName(name)

        try {
            await signUp.create({
                emailAddress: email,
                firstName,
                lastName,
                ...getMetadata(),
            })
        } catch (err) {
            makeHandleClerkError({ shouldRethrow: false, onAllErrors: makeHandleError('inline-error') })(err)
            return
        }

        // create() succeeded — the board referral is now on the Clerk signup object, so it is safe to
        // clear the stash. A failure above returns before this, leaving the stash intact for a retry.
        clearBoardReferralCode()

        try {
            await signUp.prepareEmailAddressVerification({
                strategy: 'email_code',
            })
        } catch (err) {
            makeHandleClerkError({ shouldRethrow: false, onAllErrors: makeHandleError('toast') })(err)
            return
        }

        setCurrentStep(AUTH_STEPS.CodeVerification, {
            [AUTH_STEP_QUERY_PARAMS.Email]: email,
            [AUTH_STEP_QUERY_PARAMS.AuthFlow]: 'sign-up',
            ...(oauthRedirectUrl ? { [ATTRIBUTION_QUERY_PARAMS.RedirectUrl]: oauthRedirectUrl } : {}),
        })
    })

    const handleGoogleSignUp = withGoogleSignUpLoading(async () => {
        if (!signUp) return
        if (!isAuthLoaded) return
        if (isSignedIn) {
            if (oauthRedirectUrl) {
                window.location.assign(oauthRedirectUrl)
                return
            }
            router.replace(routingUrl)
            return
        }

        if (await interceptInAppBrowser()) return

        setLocalStorageItem(LOCAL_STORAGE_KEYS.Auth.LastUsedMethod, 'google')

        // Track signup method selection in PostHog
        trackEvent(TrackEventName.SignupMethodSelected, {
            method: 'google',
        })

        pushGTMEvent({ event: 'signed_up' })

        // Pass the OAuth redirect_url through to sso-callback if present
        const ssoCallbackUrl = oauthRedirectUrl
            ? `${window.location.origin}/auth/sso-callback?redirect_url=${encodeURIComponent(oauthRedirectUrl)}`
            : `${window.location.origin}/auth/sso-callback`

        // Both sign-in and sign-up redirect to /routing, which determines the next step.
        // External OAuth flows use oauthRedirectUrl instead.
        const finalRedirectUrl = oauthRedirectUrl ? oauthRedirectUrl : window.location.origin + routingUrl

        const { getMetadata } = getMetadataMethods(boardReferralCode)

        await signUp
            .authenticateWithRedirect({
                strategy: 'oauth_google',
                redirectUrl: ssoCallbackUrl,
                redirectUrlComplete: finalRedirectUrl,
                ...getMetadata(),
            })
            .catch(makeHandleClerkError({ shouldRethrow: false, onAllErrors: makeHandleError('toast') }))
    })

    return (
        <>
            <div id="clerk-captcha" data-cl-theme="light" data-cl-size="flexible" data-cl-language="en-US" />

            <StepContainer className="max-md:pb-28">
                <div className="w-full">
                    {!isWebView && (
                        <AuthButtonWithLastUsedBadge showBadge={lastUsedMethod === 'google'}>
                            <Button
                                size="ds2-responsive"
                                variant={'outline'}
                                className="w-full rounded-[10px] border-black/10 bg-white/60 transition-colors duration-200 ease-out hover:bg-black/4 disabled:bg-white/60"
                                onClick={handleGoogleSignUp}
                                disabled={isGoogleSignUpLoading}
                                isLoading={isGoogleSignUpLoading}
                            >
                                <span className="inline-flex items-center justify-center gap-2 w-full">
                                    <Image src="/icons/google.svg" alt="Google" width={20} height={20} />
                                    Continue with Google
                                </span>
                            </Button>
                        </AuthButtonWithLastUsedBadge>
                    )}

                    {isWebView && <GoogleOauthBrowserCallout flow="sign-up" className="mb-1" />}

                    <Separator className="my-3" orientation="horizontal">
                        or
                    </Separator>

                    <InputGroup
                        required
                        placeholder="Your Work Email"
                        type="email"
                        label="Email"
                        containerClassName="mb-3 w-full"
                        onSubmit={handleSignup}
                        autoComplete="email"
                        render={({ field, renderDefaultInput }) => (
                            <AuthButtonWithLastUsedBadge showBadge={lastUsedMethod === 'email'}>
                                {renderDefaultInput(field)}
                            </AuthButtonWithLastUsedBadge>
                        )}
                        {...register('email')}
                    />

                    <InputGroup
                        required
                        placeholder="Your Full Name"
                        type="text"
                        label="Full name"
                        containerClassName="mb-3 w-full"
                        onSubmit={handleSignup}
                        autoComplete="name"
                        {...register('name')}
                    />

                    <FixedBottomCtaBar topMarginClassName="mt-0">
                        <Button
                            size="ds2-responsive"
                            variant={'ds2-primary'}
                            onClick={handleSignup}
                            disabled={isSignUpLoading}
                            isLoading={isSignUpLoading}
                            className="w-full"
                        >
                            Sign up
                        </Button>
                    </FixedBottomCtaBar>

                    <AuthInlineNavigation
                        className="mt-3"
                        text="Already have an account?"
                        ctaText="Log in"
                        onClick={() => setCurrentStep(AUTH_STEPS.SignIn)}
                    />
                </div>
            </StepContainer>
        </>
    )
}

export const getMetadataMethods = (explicitBoardReferralCode?: string | null) => {
    const getMetadata = () => {
        const trackingId = getLocalStorageItem<string>(LOCAL_STORAGE_KEYS.Attribution.TrackingId)
        const initialLandingPath = getLocalStorageItem<string>(LOCAL_STORAGE_KEYS.SignUp.InitialLandingPath)
        // Presence of `shopify_pending_shop` means the user just came through
        // the Shopify install flow (set by /shopify/welcome after charge
        // approval). The webhook reads this flag to *skip* the default
        // org/brand/credit-pool seeding so the Shopify claim endpoint can
        // either join the user to an existing shop org or create a fresh
        // one transactionally with the subscription — eliminating the
        // create-then-delete throwaway-org pattern. Boolean only; the shop
        // domain itself is not trusted client-side.
        const shopifyPendingShop = getLocalStorageItem<string>(LOCAL_STORAGE_KEYS.SignUp.ShopifyPendingShop)
        const viaShopifyInstall = !!shopifyPendingShop

        // The open board auth flow is authoritative, including an explicit absence (`null`); the TTL
        // stash remains the retry fallback only when no flow-scoped value was supplied (`undefined`).
        const boardReferralCode =
            explicitBoardReferralCode === undefined ? peekBoardReferralCode() : explicitBoardReferralCode

        // Dub click id, captured client-side after a genuine click on a Dub short link (non-board
        // funnels). Carried inside the signup so the webhook can register that click-anchored Dub
        // lead server-side instead of the racy post-org sync. Board signups don't need it: the board
        // referral code above is the single signal, and the BACKEND decides the owner's program
        // (Dub affiliate by partnerId — precedence — vs in-house referral); a fresh board code
        // always outranks this potentially-stale dub_id — see resolveBoardSignupAttribution.
        const dubClickId = getLocalStorageItem<{ clickId?: string }>(LOCAL_STORAGE_KEYS.Attribution.Dub)?.clickId

        return {
            unsafeMetadata: {
                ...(trackingId && { [ATTRIBUTION_CONSTS.ExternalIdentifierMetadataKey]: trackingId }),
                ...(initialLandingPath && { [SIGN_UP_CONSTS.InitialLandingPathMetadataKey]: initialLandingPath }),
                ...(viaShopifyInstall && { [SIGN_UP_CONSTS.ViaShopifyInstallMetadataKey]: true }),
                ...(boardReferralCode && { [SIGN_UP_CONSTS.BoardReferralCodeMetadataKey]: boardReferralCode }),
                ...(dubClickId && { [SIGN_UP_CONSTS.DubClickIdMetadataKey]: dubClickId }),
            },
        }
    }

    return { getMetadata }
}
